import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import InComponentLoader from '../Loader/componentloader';
import { readFileAsArrayBuffer } from '../../../functions';

// El worker de pdf.js se sirve como archivo estático desde /public/pdf.worker.min.js
// (copiado automáticamente en cada `npm install` por scripts/copy-pdf-worker.js),
// para no depender de un CDN externo en producción.
// Usamos el build "legacy" (tanto acá como en el worker copiado) porque el build
// moderno de pdfjs-dist usa sintaxis de clase (campos privados `#campo`) que el
// Babel/Webpack 4 de este proyecto (react-scripts 3.4.4) no puede parsear.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/*
Muestra únicamente la primera página de un PDF (objeto File seleccionado
localmente, aún no subido al backend) como imagen dentro de un <canvas>.

Reemplaza el enfoque anterior de previsualizar el PDF completo con un
<embed src="data:application/pdf;base64,..."> : con archivos grandes
(~9MB o más) el visor de PDF nativo del navegador no lograba renderizar
ese data-URI de forma confiable. Al rasterizar solo la página 1 con
pdf.js, el tamaño del documento deja de ser un problema para el preview.

Uso:
  <PdfFirstPagePreview file={this.state.selectedFile} width={500} height={450} />
*/
const PdfFirstPagePreview = props => {
  // Receive {file, width, height}
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const renderFirstPage = async () => {
      if (!props.file) return;

      setLoading(true);
      setHasError(false);

      try {
        const arrayBuffer = await readFileAsArrayBuffer(props.file);
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);

        if (isCancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const containerWidth = props.width || 500;
        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport
        }).promise;
      } catch (e) {
        if (!isCancelled) {
          setHasError(true);
          toast.error('No se pudo generar la vista previa del PDF. Puede continuar con la subida igualmente.');
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    renderFirstPage();

    // Evita actualizar el estado si el componente se desmonta o el archivo
    // cambia antes de que termine el render (usuario elige otro PDF rápido).
    return () => { isCancelled = true; };
  }, [props.file]);

  if (!props.file) return null;

  return (
    <div
      className="d-block mx-auto"
      style={{
        width: '100%',
        maxWidth: props.width || 500,
        height: props.height || 450,
        overflow: 'hidden',
        marginBottom: '15px',
        textAlign: 'center'
      }}
    >
      <InComponentLoader show={loading} message="Generando vista previa..." />
      {!hasError && (
        <canvas
          ref={canvasRef}
          style={{
            display: loading ? 'none' : 'block',
            margin: '0 auto',
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        />
      )}
    </div>
  );
};

export default PdfFirstPagePreview;
